import { Button, CircularProgress, createStyles, DialogContent, makeStyles, TextField, Theme } from '@material-ui/core';
import React, { useReducer, useState } from 'react';

import {
  useGetDocumentToImportLazyQuery,
  useGetPlexDetailsQuery,
  useImportFaqsFromGoogleDocMutation,
} from '../../../../graphql/gen/types';
import { operationName } from '../../../../graphql/operationName';
import { GET_FEED_BOARDS_QUERY } from '../../../../graphql/queries/GetFeedBoards';
import { spacings } from '../../../../theme';
import { PlexName } from '../../common/PlexName';
import { logAnalytics } from '../../shared/logAnalytics';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { CreateOrEditDialogTitle } from '../post/CreateOrEditDialogTitle';
import { useDialogState } from '../useDialogState';
import { BoardGoogleDocPreview, getFaqBoardSection, getFaqSections } from './BoardGoogleDocPreview';
import { documentToImportReducer } from './documentToImportReducer';
import { useInitializeDocumentToImportOnSuccess } from './useInitializeDocumentToImportOnSuccess';

type Props = {
  plexId: string;
};

export const ImportFaqsFromGoogleDocs = ({ plexId }: Props): React.ReactElement => {
  const classes = useStyles();
  const [, setDialogState] = useDialogState();
  const [googleDocLink, setGoogleDocLink] = useState('');
  const { data: plexData } = useGetPlexDetailsQuery({ variables: { plexId } });
  // TODO: error handling
  const [getDocumentToImport, { data, loading }] = useGetDocumentToImportLazyQuery({ fetchPolicy: 'no-cache' });
  // TODO: error handling
  const [importFaqsFromGoogleDoc, { loading: importFaqsFromGoogleDocLoading }] = useImportFaqsFromGoogleDocMutation();
  const [documentToImport, dispatch] = useReducer(documentToImportReducer, undefined);

  const handleImport = async (): Promise<void> => {
    if (documentToImport == null) {
      getDocumentToImport({ variables: { url: googleDocLink } });
    } else {
      const { selectedSections, content, externalEntityId } = documentToImport;
      const boardSection = getFaqBoardSection({ selectedSections });

      if (boardSection != null) {
        const faqSections = getFaqSections({ selectedSections });
        const { data } = await importFaqsFromGoogleDoc({
          variables: {
            input: {
              plexId,
              boardName: boardSection.title,
              importedQuestions: [...faqSections].map((faqSection) => {
                return {
                  question: faqSection.title,
                  answer: content.slice(faqSection.startIndex + 1, faqSection.endIndex),
                };
              }),
              externalEntityId,
            },
          },
          refetchQueries: [operationName(GET_FEED_BOARDS_QUERY)],
          awaitRefetchQueries: true,
        });
        const boardId = data?.importBoard?.id;
        if (boardId != null) {
          logAnalytics({ name: 'created board', details: { plexId, itemId: boardId } });
          setDialogState(null, 'replace');
        }
      }
    }
  };

  useInitializeDocumentToImportOnSuccess({ data, dispatch });

  if (plexData?.loggedIn?.plexById == null) {
    return <CircularProgress size={24} />;
  }

  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexName
          name={plexData.loggedIn.plexById.name}
          color={plexData.loggedIn.plexById.color}
          className={classes.plexName}
        />
      </DialogTopBar>

      <CreateOrEditDialogTitle />

      <DialogContent>
        <TextField
          value={googleDocLink}
          fullWidth
          variant="outlined"
          label="Google document link"
          name="Google document link"
          placeholder="https://docs.google.com/document/d/.../"
          onChange={(e): void => {
            setGoogleDocLink(e.currentTarget.value);
            dispatch({ type: 'INIT', state: undefined });
          }}
        />

        <BoardGoogleDocPreview dispatch={dispatch} documentToImport={documentToImport} data={data} loading={loading} />
      </DialogContent>
      <DialogActions
        cta={
          <Button
            variant="contained"
            color="primary"
            disabled={loading || importFaqsFromGoogleDocLoading}
            onClick={handleImport}
          >
            {documentToImport == null ? 'Preview' : 'Import'}
          </Button>
        }
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
    documentPreview: {
      marginTop: spacings.normal,
      padding: spacings.normal,
      textAlign: 'center',
    },
  })
);
